import type { JSX } from 'react'

/**
 * Shown until the first snapshot arrives.
 *
 * Previously the room rendered a fabricated empty room while connecting, so you
 * saw a full starting position and a 10:00 clock that then jumped to the real
 * values — and on a rematch series the arriving snapshot even fired the
 * game-start sound for a game that had started minutes ago. A skeleton is
 * honest about knowing nothing yet.
 */
export const BoardSkeleton = ({ label = 'Connecting…' }: { readonly label?: string }): JSX.Element => (
  <div className="mx-auto flex w-full max-w-6xl flex-col gap-3 lg:flex-row lg:items-start lg:p-4">
    <div className="flex w-full min-w-0 flex-col lg:max-w-[min(100%,calc(100vh-11rem))]">
      <div className="flex items-center justify-between px-2 py-2 sm:px-0">
        <div className="skeleton h-7 w-32 rounded bg-panel-raised" />
        <div className="skeleton h-9 w-20 rounded bg-panel-raised" />
      </div>

      <div className="skeleton relative aspect-square w-full overflow-hidden rounded">
        <div className="absolute inset-0 grid grid-cols-8 grid-rows-8">
          {Array.from({ length: 64 }, (_, index) => {
            const light = ((index >> 3) + (index % 8)) % 2 === 1
            return (
              <div key={index} className={light ? 'bg-board-light/40' : 'bg-board-dark/40'} />
            )
          })}
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="rounded-full bg-black/60 px-3 py-1.5 text-xs font-semibold text-white">
            {label}
          </span>
        </div>
      </div>

      <div className="flex items-center justify-between px-2 py-2 sm:px-0">
        <div className="skeleton h-7 w-32 rounded bg-panel-raised" />
        <div className="skeleton h-9 w-20 rounded bg-panel-raised" />
      </div>
    </div>

    <div className="w-full px-2 sm:px-0 lg:w-80 lg:px-0">
      <div className="skeleton h-56 w-full rounded-panel bg-panel" />
    </div>
  </div>
)
